import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus } from 'lucide-react';
import { apiCall, setJwt, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

// Platform Super-Admin login — reached only via the /superadmin URL, never linked
// from the regular store login screen. Auth is a single ADMIN_SECRET (server-side
// .env value, never shipped to the client), not a user account — see adminLogin
// in api/routes.php. Real access control happens server-side; this form is just
// the UI path to reach it.
export const SuperAdminLogin = ({ onLogin }) => {
    const [secret, setSecret] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        const res = await apiCall('adminLogin', { secret });
        setLoading(false);
        if (res.error) { setError(res.error); setSecret(''); return; }
        setJwt(res.token);
        onLogin({ role: 'superadmin', name: 'Super Admin', user_id: 'superadmin' });
    };

    return (
        <div className="h-screen w-screen flex items-center justify-center bg-[#0d0f11] p-4">
            <div className="bg-[#1a1e22] p-8 rounded-xl shadow-md border border-[#2a2f34] max-w-sm w-full text-center">
                <div className="w-14 h-14 bg-[#4ade80]/10 text-[#4ade80] rounded-full flex items-center justify-center mx-auto mb-5"><Crown size={28}/></div>
                <h2 className="text-lg font-bold text-white mb-1">Platform Admin</h2>
                <p className="text-[#9aa4ac] mb-6 text-xs">Enter the admin secret to manage tenants</p>
                <form onSubmit={handleSubmit} className="space-y-3 text-left">
                    <input
                        type="password"
                        required
                        autoFocus
                        placeholder="Admin secret"
                        value={secret}
                        onChange={e => setSecret(e.target.value)}
                        className="w-full p-3 bg-[#0d0f11] border border-[#2a2f34] rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-[#4ade80]"
                        autoComplete="off"
                    />
                    {error && <p className="text-[#f87171] text-xs font-semibold">{error}</p>}
                    <button type="submit" disabled={loading} className="w-full bg-[#4ade80] text-[#062411] py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                        {loading ? 'Verifying…' : 'Enter'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const LoginScreen = ({ users, onLogin, refreshData }) => {
    const adminExists = users.some(u => u && u.role === 'admin');
    const [pin, setPin] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [setupStep, setSetupStep] = useState(1);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset PIN flow: null = hidden, 'email' = ask email, 'otp' = ask otp + new pin
    const [resetStep, setResetStep] = useState(null);
    const [resetEmail, setResetEmail] = useState('');
    const [resetOtp, setResetOtp] = useState('');
    const [newPin, setNewPin] = useState('');
    const [resetMsg, setResetMsg] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        // Match username first, then verify PIN
        if (!username.trim()) { setLoading(false); setError('Please enter your username.'); return; }
        const matchedUser = users.find(u => u && (u.name || '').toLowerCase().trim() === username.toLowerCase().trim());
        if (!matchedUser) { setLoading(false); setError('Username not found.'); setPin(''); return; }
        // Username is verified server-side too — the client-side check above is UX only
        const res = await apiCall('loginPin', { pin: String(pin), username: username.trim() });
        setLoading(false);
        if (res.error === 'PIN_RESET_REQUIRED') {
            setError('Admin PIN needs to be reset. Use "Forgot PIN?" below.');
        } else if (res.error) {
            setError('Incorrect PIN.');
            setPin('');
        } else {
            // Ensure the logged-in user matches the entered username
            if ((res.user?.name || '').toLowerCase().trim() !== username.toLowerCase().trim()) {
                setError('Username and PIN do not match.');
                setPin('');
            } else {
                if (res.token) setJwt(res.token); // store JWT for all subsequent API calls
                onLogin(res.user);
            }
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        const res = await apiCall('sendAdminOTP', { email });
        setLoading(false);
        if (res.error) {
            if(res.error.includes("Fallback OTP")) { setError(res.error); setSetupStep(2); } 
            else { setError(res.error); }
        } else {
            setSetupStep(2); setError('Verification code sent to your email.'); 
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        if (pin.length !== 4) return setError("PIN must be 4 digits.");
        setLoading(true); setError('');
        const res = await apiCall('createAdmin', { email, otp, name, pin });
        setLoading(false);
        if (res.error) setError(res.error);
        else await refreshData();
    };

    // --- RESET PIN HANDLERS ---
    const handleSendResetOtp = async (e) => {
        e.preventDefault(); setLoading(true); setResetMsg('');
        const res = await apiCall('sendResetOTP', { email: resetEmail });
        setLoading(false);
        const isMailFallback = res.error && res.error.includes('Dev OTP:');
        if (res.error && !isMailFallback) {
            setResetMsg(res.error);
        } else {
            setResetStep('otp');
            setResetMsg(isMailFallback ? '⚠️ Email unavailable. ' + res.error : 'OTP sent! Check your email.');
        }
    };

    const handleVerifyResetOtp = async (e) => {
        e.preventDefault();
        if (newPin.length !== 4) { setResetMsg('PIN must be exactly 4 digits.'); return; }
        setLoading(true); setResetMsg('');
        const res = await apiCall('verifyResetOTP', { email: resetEmail, otp: resetOtp, pin: newPin });
        setLoading(false);
        if (res.error) {
            setResetMsg(res.error);
        } else {
            setResetSuccess(true);
            setResetMsg('PIN reset successfully! You can now log in.');
            await refreshData();
        }
    };

    const closeReset = () => {
        setResetStep(null); setResetEmail(''); setResetOtp('');
        setNewPin(''); setResetMsg(''); setResetSuccess(false);
    };

    if (!adminExists && users.length > -1) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-[#f6f7f8] p-4">
                <div className="bg-white p-8 rounded-xl shadow-md border border-[#e1e3e5] max-w-sm w-full text-center">
                    <div className="w-16 h-16 bg-[#e4fcf4] text-[#008060] rounded-full flex items-center justify-center mx-auto mb-6"><UserPlus size={32}/></div>
                    <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">Master Setup</h2>
                    <p className="text-[#5c5f62] mb-6 text-sm">No Super Admin detected. Create the master account.</p>
                    
                    {setupStep === 1 ? (
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <input type="email" required placeholder="Admin Email Address" value={email} onChange={e=>setEmail(e.target.value)} className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm"/>
                            {error && <p className={`text-xs font-bold ${error.includes('Fallback') ? 'text-[#b98900]' : 'text-[#d82c0d]'}`}>{error}</p>}
                            <button type="submit" disabled={loading} className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold hover:bg-[#006e52] transition-colors disabled:opacity-50">Send Setup Code</button>
                        </form>
                    ) : (
                        <form onSubmit={handleCreateAdmin} className="space-y-4">
                            <input type="text" required placeholder="Your Name" value={name} onChange={e=>setName(e.target.value)} className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm"/>
                            <input type="text" required placeholder="6-Digit Email OTP" value={otp} onChange={e=>setOtp(e.target.value)} className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm font-mono text-center"/>
                            <input type="password" required maxLength="4" placeholder="Create 4-Digit PIN" value={pin} onChange={e=>setPin(e.target.value)} className="w-full text-center text-xl font-bold tracking-[0.5em] p-3 border border-[#c9cccf] rounded-lg"/>
                            {error && <p className="text-[#d82c0d] text-xs font-bold">{error}</p>}
                            <button type="submit" disabled={loading} className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold hover:bg-[#006e52] transition-colors disabled:opacity-50">Secure System</button>
                        </form>
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <div className="h-screen w-screen flex items-center justify-center bg-[#f6f7f8] p-4 overflow-y-auto">
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#e1e3e5] max-w-sm w-full text-center">
                <div className="w-14 h-14 bg-[#e4fcf4] text-[#008060] rounded-full flex items-center justify-center mx-auto mb-4"><Lock size={28}/></div>
                <h2 className="text-lg font-bold text-[#1a1a1a] mb-1">OPS — Purely Organic</h2>
                <p className="text-[#5c5f62] mb-5 text-xs">Enter your username and PIN to sign in</p>
                
                <form onSubmit={handleLogin} className="space-y-3">
                    <div className="text-left">
                        <label className="text-xs font-semibold text-[#5c5f62] uppercase block mb-1">Username</label>
                        <input 
                            type="text" 
                            placeholder="Your name..." 
                            value={username} 
                            onChange={e=>setUsername(e.target.value)} 
                            className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm font-semibold text-[#1a1a1a] outline-none focus:ring-2 focus:ring-[#008060]" 
                            autoFocus 
                            autoComplete="username"
                        />
                    </div>
                    <div className="text-left">
                        <label className="text-xs font-semibold text-[#5c5f62] uppercase block mb-1">PIN</label>
                        <input 
                            type="password" 
                            placeholder="••••" 
                            value={pin} 
                            onChange={e=>setPin(e.target.value)} 
                            className="w-full text-center text-2xl font-bold tracking-[1em] p-3 border border-[#c9cccf] rounded-lg text-[#1a1a1a] outline-none focus:ring-2 focus:ring-[#008060]" 
                            maxLength={4}
                            autoComplete="current-password"
                        />
                    </div>
                    {error && <p className="text-[#d82c0d] text-sm font-semibold">{error}</p>}
                    <button type="submit" disabled={loading} className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold text-base hover:bg-[#006e52] transition-colors shadow-sm disabled:opacity-50">{loading ? 'Verifying…' : 'Sign In'}</button>
                </form>
                <button onClick={() => setResetStep('email')} className="mt-3 text-xs text-[#5c5f62] hover:text-[#008060] transition-colors underline underline-offset-2">
                    Forgot PIN? Reset via Email
                </button>
            </div>

            {/* RESET PIN MODAL */}
            {resetStep && (
                <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-sm rounded-xl shadow-lg border border-[#e1e3e5] p-6 text-center">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#fff3cd] text-[#b98900] rounded-full flex items-center justify-center"><Unlock size={16}/></div>
                                <h3 className="font-bold text-[#1a1a1a] text-base">Reset PIN</h3>
                            </div>
                            <button onClick={closeReset} className="text-[#5c5f62] hover:text-[#1a1a1a]"><X size={20}/></button>
                        </div>

                        {resetSuccess ? (
                            <div className="py-4">
                                <div className="w-14 h-14 bg-[#e4fcf4] text-[#008060] rounded-full flex items-center justify-center mx-auto mb-4"><UserCheck size={28}/></div>
                                <p className="text-[#008060] font-semibold text-sm mb-4">{resetMsg}</p>
                                <button onClick={closeReset} className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold hover:bg-[#006e52] transition-colors">Back to Login</button>
                            </div>
                        ) : resetStep === 'email' ? (
                            <form onSubmit={handleSendResetOtp} className="space-y-4 text-left">
                                <p className="text-[#5c5f62] text-sm">Enter the admin email address. We'll send a one-time code to reset your PIN.</p>
                                <div>
                                    <label className="text-xs font-semibold text-[#5c5f62] uppercase mb-1 block">Admin Email</label>
                                    <input type="email" required placeholder="admin@example.com" value={resetEmail} onChange={e=>setResetEmail(e.target.value)} className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm" autoFocus/>
                                </div>
                                {resetMsg && <p className="text-[#d82c0d] text-xs font-semibold">{resetMsg}</p>}
                                <button type="submit" disabled={loading} className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold hover:bg-[#006e52] transition-colors disabled:opacity-50">
                                    {loading ? 'Sending…' : 'Send OTP to Email'}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleVerifyResetOtp} className="space-y-4 text-left">
                                <p className="text-[#5c5f62] text-sm">Enter the 6-digit OTP sent to <span className="font-semibold text-[#1a1a1a]">{resetEmail}</span> and set your new PIN.</p>
                                <div>
                                    <label className="text-xs font-semibold text-[#5c5f62] uppercase mb-1 block">6-Digit OTP</label>
                                    <input type="text" required placeholder="123456" maxLength="6" value={resetOtp} onChange={e=>setResetOtp(e.target.value)} className="w-full p-3 border border-[#c9cccf] rounded-lg text-sm font-mono text-center tracking-widest" autoFocus/>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-[#5c5f62] uppercase mb-1 block">New 4-Digit PIN</label>
                                    <input type="password" required maxLength="4" placeholder="••••" value={newPin} onChange={e=>setNewPin(e.target.value)} className="w-full text-center text-2xl font-bold tracking-[1em] p-3 border border-[#c9cccf] rounded-lg"/>
                                </div>
                                {resetMsg && <p className={`text-xs font-semibold ${resetMsg.includes('sent') ? 'text-[#008060]' : 'text-[#d82c0d]'}`}>{resetMsg}</p>}
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => { setResetStep('email'); setResetOtp(''); setNewPin(''); setResetMsg(''); }} className="flex-1 py-3 rounded-lg font-semibold text-sm border border-[#c9cccf] text-[#5c5f62] hover:bg-[#f6f7f8] transition-colors">← Back</button>
                                    <button type="submit" disabled={loading} className="flex-1 bg-[#008060] text-white py-3 rounded-lg font-semibold hover:bg-[#006e52] transition-colors disabled:opacity-50 text-sm">
                                        {loading ? 'Verifying…' : 'Reset PIN'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginScreen;
